import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../../../../constants';
import RacePlannerAptitudes from './RacePlannerAptitudes';
import { useRaceEngineContext } from '../RaceEngine/RaceEngineContext';

const RacePlannerCharacterDisplay = ({ character, cards, selectedCard, onCardChange }) => {
    const { setSelectedAptitudes } = useRaceEngineContext();
    const [rawAptitudes, setRawAptitudes]       = useState([]);
    const [simulatedAptitudes, setSimulatedAptitudes] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const res  = await fetch(`${API_BASE_URL}/aptitudes?Id_Personaje=${character.Id_Personaje}`);
                const data = await res.json();
                setRawAptitudes(data);
            } catch (err) { console.error('Error fetching aptitudes:', err); }
        })();
    }, [character.Id_Personaje]);

    useEffect(() => {
        if (!selectedCard || rawAptitudes.length === 0) return;
        const match = rawAptitudes.find(a => a.card_id === selectedCard.card_id)
                   || rawAptitudes.find(a => !a.card_id)
                   || rawAptitudes[0];
        setSimulatedAptitudes(match || {});
    }, [selectedCard, rawAptitudes]);

    const handleAptitudeChange = (key, rank) => {
        const updated = { ...simulatedAptitudes, [key]: rank };
        setSimulatedAptitudes(updated);
        setSelectedAptitudes(updated);
    };

    const getImageUrl = (card) => {
        if (!character || !card) return '';
        const sl = s => (typeof s === 'string' ? s.replace(/[\\/*?"<>|]/g, '') : '');
        try {
            const name = `${sl(character.name_en).replace(/ /g,'_')}_${sl(card.reference).replace(/ /g,'_')}_${sl(card.title_en||'').replace(/ /g,'_')}.png`;
            return `${API_BASE_URL}/api/thumb?img=/images/character/${name}&width=512`;
        } catch { return ''; }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
            {/* ── Card shell with gradient border ─────────────────────── */}
            <motion.div 
                animate={{ backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'] }}
                transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
                className="relative rounded-[30px] p-[1.5px] shadow-2xl overflow-hidden
                    bg-gradient-to-br from-[#FAB0C0]/60 via-[#48E5B9]/20 to-[#48E5B9]/60
                    dark:from-[#FAB0C0]/40 dark:via-gray-700/50 dark:to-[#48E5B9]/40 bg-[length:200%_200%]"
            >
                {/* Inner Glow */}
                <div className="absolute inset-0 bg-white/40 dark:bg-black/20" />

                <div className="relative rounded-[28.5px] bg-white/90 dark:bg-gray-950/90 backdrop-blur-3xl overflow-hidden z-10 h-full">
                    <div className="flex flex-col lg:flex-row h-full">

                        {/* ── LEFT: image panel ───────────────────────────── */}
                        <div className="relative w-full lg:w-[260px] xl:w-[300px] shrink-0 min-h-[300px] lg:min-h-[460px] overflow-hidden">
                            {/* Animated Background Mesh */}
                            <motion.div animate={{ opacity: [0.3, 0.6, 0.3] }} transition={{ duration: 4, repeat: Infinity }}
                                className="absolute -inset-20 bg-[radial-gradient(ellipse_at_top_right,rgba(72,229,185,0.15),transparent_50%),radial-gradient(ellipse_at_bottom_left,rgba(250,176,192,0.15),transparent_50%)] dark:bg-[radial-gradient(ellipse_at_top_right,rgba(72,229,185,0.05),transparent_50%),radial-gradient(ellipse_at_bottom_left,rgba(250,176,192,0.05),transparent_50%)]"
                            />

                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={selectedCard?.card_id}
                                    initial={{ opacity: 0, scale: 1.05 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.4 }}
                                    src={getImageUrl(selectedCard)}
                                    alt={character.name_en}
                                    className="absolute inset-0 w-full h-full object-cover object-top"
                                    onError={e => { e.target.style.opacity = '0'; }}
                                />
                            </AnimatePresence>

                            {/* Fade to card bg on bottom + right */}
                            <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-gray-950 via-transparent to-transparent lg:hidden z-10" />
                            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-white dark:from-gray-950 to-transparent hidden lg:block z-10 opacity-70" />
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-white dark:to-gray-950 hidden lg:block z-10" />

                            {/* UID badge */}
                            <div className="absolute top-4 left-4 z-10">
                                <span className="px-2.5 py-1 rounded-full text-[9px] font-black tracking-[0.25em] uppercase
                                    bg-white/70 dark:bg-black/50 backdrop-blur-md
                                    text-[#48E5B9] border border-[#48E5B9]/30">
                                    UID {character.Id_Personaje.toString().padStart(4, '0')}
                                </span>
                            </div>
                        </div>

                        {/* ── RIGHT: info panel ───────────────────────────── */}
                        <div className="relative flex-1 flex flex-col gap-6 p-6 lg:p-7 z-20">

                            {/* Character name */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 2, repeat: Infinity }}
                                        className="w-1.5 h-1.5 rounded-full bg-[#48E5B9]" />
                                    <span className="text-[9px] font-black tracking-[0.3em] text-[#48E5B9] uppercase">
                                        Planning Active
                                    </span>
                                </div>
                                <h2 className="text-4xl font-black tracking-tighter text-gray-900 dark:text-white leading-none">
                                    {character.name_en}
                                </h2>
                                <motion.div
                                    initial={{ width: 0 }} animate={{ width: '48px' }}
                                    transition={{ delay: 0.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                                    className="h-px mt-4 bg-gradient-to-r from-[#FAB0C0] to-transparent"
                                />
                            </div>

                            {/* Costume selector */}
                            <div>
                                <p className="text-[8px] font-black uppercase tracking-[0.38em] text-gray-400 dark:text-gray-500 mb-2.5">
                                    Costume
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {cards.map(card => {
                                        const active = selectedCard?.card_id === card.card_id;
                                        return (
                                            <button key={card.card_id} onClick={() => onCardChange(card)}
                                                className={`
                                                    px-3.5 py-1.5 rounded-xl text-[9px] font-black tracking-wider uppercase
                                                    transition-all duration-300
                                                    ${active
                                                        ? 'bg-[#48E5B9] text-black shadow-[0_0_16px_rgba(72,229,185,0.4)]'
                                                        : 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                                                    }
                                                `}>
                                                {card.title_en || card.reference}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Aptitudes */}
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-5">
                                    <p className="text-[8px] font-black uppercase tracking-[0.38em] text-gray-400 dark:text-gray-500 shrink-0">
                                        Aptitudes
                                    </p>
                                    <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
                                </div>
                                <RacePlannerAptitudes
                                    aptitudes={simulatedAptitudes}
                                    onAptitudeChange={handleAptitudeChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default RacePlannerCharacterDisplay;
